'use client'

import { GlobalTypography } from 'cms-types'
import { createContext, createElement, PropsWithChildren, useContext } from 'react'

type GlobalTypographyState = Omit<GlobalTypography, 'id' | 'createdAt' | 'updatedAt'>

const defaultGlobalTypography: GlobalTypographyState = {
  magicLinksToastsCustomization: {
    products: {
      success: '',
      error: '',
    },
    discounts: {
      success: '',
      error: '',
    },
    giftCards: {
      allCardsSuccess: '',
      oneCardSuccess: '',
      invalidCardError: '',
      cardAlreadyAppliedError: '',
    },
  },
  floatingButton: {
    phone: '',
    message: '',
  },
  productPage: {
    accessibility: {
      price: '',
      compareAtPrice: '',
    },
    addToCartButtonText: '',
    buyNowButtonText: '',
    showBuyNowButton: true,
    showShopPayButton: true,
    shoppingButtonLabel: '',
    reviewsText: '',
    reviewsBlock: {
      title: '',
      fromText: '',
      verifiedCustomerText: '',
    },
    itemIsOutOfStockButtonText: '',
    itemIsOutOfStockEmailNotificationText: {
      root: {
        type: 'root',
        format: '',
        indent: 0,
        version: 1,
        children: [],
        direction: 'ltr',
      },
    },
    itemIsOutOfStockEmailNotificationInput: '',
    itemIsOutOfStockEmailNotificationNewsletterCheckboxText: '',
    itemIsOutOfStockEmailNotificationButtonText: '',
    itemIsOutOfStockEmailNotificationSuccessMessage: '',
    itemIsOutOfStockEmailNotificationSuccessMessageNewsletterAdded: '',
    outOfStockTag: {
      title: '',
    },
    backButton: {
      fallbackLink: {
        title: '',
        page: undefined,
      },
      backLinkText: '',
    },
  },
  blogPostPage: {
    previewLinkTitle: '',
  },
  ingredientsPage: {
    blogPostLinkTitle: '',
    ingredientPageLinkTitle: '',
    containedInProductsTitle: '',
    importantIngredientsTitle: '',
    restIngredientsTitle: '',
    searchBarLabel: '',
    maxItemsPerPage: 0,
    pageTitle: '',
    pageDescription: '',
    navigationTitle: '',
  },
  authSection: {
    accountPageTitle: '',
    logInPageTitle: '',
    signUpPageTitle: '',
    recoverPasswordPageTitle: '',
    logInButton: '',
    emailText: '',
    passwordText: '',
    passwordConfirmText: '',
    firstNameText: '',
    lastNameText: '',
    phoneNumberText: '',
    receiveMarketingText: '',
    passwordRecoveryInstructionsText: '',
    ordersTabTitle: '',
    addressesTabTitle: '',
    isNotValidText: '',
    notFoundText: '',
    isRequiredText: '',
    isTooShortText: '',
    invalidCredentialsText: '',
    somethingWentWrongText: '',
    signUpButton: '',
    recoverPasswordButton: '',
    submitButtonText: '',
    cancelButton: '',
    ordersTab: {
      orderIdColumnTitle: '',
      orderDateColumnTitle: '',
      orderTotalColumnTitle: '',
      orderStatusColumnTitle: '',
      orderDeliveryColumnTitle: '',
      orderActionsColumnTitle: '',
      orderTitleColumnTitle: '',
      orderPriceColumnTitle: '',
      orderQuantityColumnTitle: '',
      reorderButtonText: '',
      viewDetailsButtonText: '',
    },
    addressesTab: {
      addAddressButton: '',
      deleteAddressButton: '',
      editAddressButton: '',
      defaultAddressText: '',
      editAddressTitle: '',
      countryText: '',
      cityText: '',
      provinceText: '',
      zipCodeText: '',
      addressLine1Text: '',
      addressLine2Text: '',
      phoneNumberText: '',
      companyText: '',
      makeDefaultAddressText: '',
      deleteAddressConfirmationText: '',
    },
  },
  clubTiers: {
    bronze: '',
    silver: '',
    gold: '',
  },
}

const GlobalTypographyContext = createContext<GlobalTypographyState>(defaultGlobalTypography)

export function GlobalTypographyProvider({
  value,
  children,
}: PropsWithChildren<{ value: GlobalTypographyState }>) {
  return createElement(GlobalTypographyContext.Provider, { value }, children)
}

export function useGlobalTypography(): GlobalTypographyState
export function useGlobalTypography<T>(selector: (state: GlobalTypographyState) => T): T
export function useGlobalTypography<T>(selector?: (state: GlobalTypographyState) => T) {
  const state = useContext(GlobalTypographyContext)

  return selector ? selector(state) : state
}
